import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageSpinner } from "@/components/ui/spinner";
import { statusColor, formatDate, formatDateTime } from "@/lib/utils";
import { X, ExternalLink, MapPin, User, Briefcase, Clock, FileText } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

interface JobQuickViewProps {
    jobId: string;
    onClose: () => void;
}

export function JobQuickView({ jobId, onClose }: JobQuickViewProps) {
    const navigate = useNavigate();

    const { data, isLoading } = useQuery({
        queryKey: ["job-quick", jobId],
        queryFn: () => api.get<{ job: any }>(`/api/jobs/${jobId}`),
        enabled: !!jobId,
    });

    const job = data?.job;

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
            <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-y-auto border-l bg-card shadow-xl">
                {/* eslint-disable-next-line no-restricted-syntax -- D1: sticky header — content scrolls beneath it; the blur is load-bearing */}
                <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-card/95 backdrop-blur px-4 py-3">
                    <h2 className="text-lg font-semibold truncate">Quick View</h2>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                navigate(`/jobs/${jobId}`);
                                onClose();
                            }}
                        >
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                        </Button>
                        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-8">
                        <PageSpinner />
                    </div>
                ) : !job ? (
                    <div className="p-8 text-center text-foreground/80">Job not found</div>
                ) : (
                    <div className="p-4 space-y-4">
                        <div>
                            <h3 className="text-xl font-bold">{job.title}</h3>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Badge className={statusColor(job.status)} variant="outline">
                                    {job.status?.replace(/_/g, " ")}
                                </Badge>
                                {job.trade_name && <Badge variant="outline">{job.trade_name}</Badge>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-start gap-2">
                                <Briefcase className="h-4 w-4 text-foreground/80 mt-0.5" />
                                <div>
                                    <p className="text-xs text-foreground/80 uppercase">Customer</p>
                                    <p className="font-medium">{job.company_name || "—"}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <User className="h-4 w-4 text-foreground/80 mt-0.5" />
                                <div>
                                    <p className="text-xs text-foreground/80 uppercase">Technician</p>
                                    <p className="font-medium">{job.assigned_to_name || "Unassigned"}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2 col-span-2">
                                <MapPin className="h-4 w-4 text-foreground/80 mt-0.5" />
                                <div>
                                    <p className="text-xs text-foreground/80 uppercase">Location</p>
                                    {job.site_id ? (
                                        <Link
                                            to={`/sites/${job.site_id}`}
                                            className="font-medium text-primary hover:underline"
                                            onClick={onClose}
                                        >
                                            {job.site_label || job.location_name || "—"}
                                        </Link>
                                    ) : (
                                        <p className="font-medium">{job.site_label || job.location_name || "—"}</p>
                                    )}
                                </div>
                            </div>
                            {job.scheduled_start && (
                                <div className="flex items-start gap-2 col-span-2">
                                    <Clock className="h-4 w-4 text-foreground/80 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-foreground/80 uppercase">Scheduled</p>
                                        <p className="font-medium">{formatDateTime(job.scheduled_start)}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {job.notes && (
                            <div className="rounded-lg border p-3">
                                <p className="text-xs text-foreground/80 uppercase mb-1 flex items-center gap-1">
                                    <FileText className="h-3 w-3" /> Notes
                                </p>
                                <p className="text-sm whitespace-pre-wrap">{job.notes}</p>
                            </div>
                        )}

                        <div className="text-xs text-foreground/80 space-y-0.5">
                            <p>Created: {formatDate(job.created_at)}</p>
                            {job.updated_at && <p>Updated: {formatDate(job.updated_at)}</p>}
                        </div>

                        <div className="border-t pt-3 flex gap-2">
                            <Button
                                className="flex-1"
                                onClick={() => {
                                    navigate(`/jobs/${jobId}`);
                                    onClose();
                                }}
                            >
                                Full Details
                            </Button>
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    navigate(`/jobs/${jobId}/edit`);
                                    onClose();
                                }}
                            >
                                Edit
                            </Button>
                        </div>

                        <div className="text-[10px] text-foreground/80 text-center">
                            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">j</kbd>/
                            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">k</kbd> navigate &middot;
                            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> open &middot;
                            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Esc</kbd> close
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
